import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Icon, IconButton, InputAdornment, Paper, Snackbar, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import LayersActionMenu from './LayersActionMenu';
import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import DialogPublicLayer from '../controls/dialogs/DialogPublicLayer';
import DialogConfirm from '../controls/dialogs/DialogConfirm';

import { LayerPublic } from '../../models/maps/layerPublic';

import { authService } from '../../services/auth.service';
import { organisatieService } from '../../services/organisaties.service';
import { planManagementService } from '../../services/plan-management.service';
import { LayerAsset } from '../../models/maps/layerAsset';
import { LayerType } from '../../models/types/layerType';
import { LayerViewModel } from '../../models/maps/layerViewModel';
import { ListParams } from '../../models/listParams';

declare module '@mui/x-data-grid' {
  interface NoRowsOverlayPropsOverrides {
    message?: string;
  }
}


const CustomNoRowsOverlay = ({ message }: { message?: string }) => (
  <Paper
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      backgroundColor: 'transparent',
    }}
    elevation={0}
  >
    <Typography variant="body1">{message ?? 'Geen kaartlagen gevonden'}</Typography>
  </Paper>
);

interface LayersProps {
  type: LayerType;
}

const Layers: React.FC<LayersProps> = ({ type }) => {
  const navigate = useNavigate();
  const [layers, setLayers] = useState<LayerViewModel[]>([]);

  const [searchText, setSearchText] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openActiveConfirm, setOpenActiveConfirm] = useState(false);
  const [openPlanShareConfirm, setOpenPlanShareConfirm] = useState(false);
  const [selectedPublicLayerId, setSelectedPublicLayerId] = useState<string | null>(null);
  const [selectedPublicLayerActive, setSelectedPublicLayerActive] = useState<boolean>(false);
  const [selectedAssetPlanShareLayerId, setSelectedAssetPlanShareLayerId] = useState<string | null>(null);
  const [selectedAssetUsePlanShareSettings, setSelectedAssetUsePlanShareSettings] = useState<boolean>(false);

  useEffect(() => {
    if (authService.isAdmin()) {
      organisatieService.getAllOrganisatiesCached();
    }
    setReady(true);
  }, []); // Empty dependency array ensures this runs once when the component mounts

  const clearSearchText = () => {
    setSearchText('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleAdd = async (layer: LayerViewModel) => {
    try {
      setLoading(true);
      const layerPublic: LayerPublic = {
        id: layer.id,
        url: layer.url ?? '',
        sourceName: layer.sourceName ?? '',
        layerDisplayName: layer.displayName ?? '',
        layerName: layer.layerName ?? '',
        description: layer.description,
        type: layer.type as 'wms' | 'wfs' | 'geopackage' | 'ogc_features' | 'ogc_tiles',
        active: layer.active ?? true,
      };
      const response = await organisatieService.addPublicMap(layerPublic);
      if (response?.ok) {
        setSnackbarMessage('Kaartlaag succesvol toegevoegd');
        setOpenSnackbar(true);
        fetchLayers();
      }
    }
    catch (err) {
      console.error('Error adding kaartlaag:', err);
      setError('Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  };

  const mapPublicLayer = (layer: LayerPublic): LayerViewModel => ({
    id: layer.id,
    sourceName: layer.sourceName,
    layerName: layer.layerName,
    displayName: layer.layerDisplayName,
    description: layer.description,
    url: layer.url,
    lastModified: layer.lastModified,
    active: layer.active,
    type: layer.type,
    isAssetLayer: false,
  });
  
  const mapAssetLayer = (layer: LayerAsset): LayerViewModel => ({
    id: layer.id ?? '',
    sourceLayerName: layer.sourceLayerName ?? '',
    sourceName: layer.sourceName
      ?? (layer.sourceOrganizationId
        ? organisatieService.getOrganisatieNaam(layer.sourceOrganizationId)
        : ''),
    layerName: layer.layerDisplayName ?? '',
    displayName: layer.layerDisplayName ?? '',
    description: layer.description,
    url: '',
    lastModified: layer.lastModified,
    active: true,
    type: 'geopackage',
    organizationId: layer.sourceOrganizationId,
    organizationName: layer.sourceOrganizationId
      ? organisatieService.getOrganisatieNaam(layer.sourceOrganizationId)
      : undefined,
    assetCount: layer.assetCount,
    usePlanShareSettings: layer.usePlanShareSettings,
    isAssetLayer: true,
  });

  const fetchLayers = useCallback(async (): Promise<LayerViewModel[]> => {
    setLoading(true);
  
    try {
      let mappedItems: LayerViewModel[] = [];
  
      const { page, pageSize } = paginationModel;
  
      if (type === LayerType.PUBLIC) {
        const params: ListParams = {
          _offset: page * pageSize,
          _limit: pageSize,
          _search: searchText.trim() || undefined,
        };
  
        const data = await organisatieService.getPublicLayers(params);
  
        mappedItems = data.items.map(mapPublicLayer);
        setTotalCount(data.count);
      }
      else {
        const params: ListParams = {
          _offset: page * pageSize,
          _limit: pageSize,
        };
  
        const data = await planManagementService.getAssetLayers(params);
  
        mappedItems = data.items.map(mapAssetLayer);
        setTotalCount(data.count);
      }
  
      setLayers(mappedItems);
      return mappedItems;
    } 
    catch (error: any) {
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
  
      setError(error.message || 'Er is een fout opgetreden');
      return [];
    } 
    finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchText, type]);

  useEffect(() => {
    if (!ready) return;
    fetchLayers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, fetchLayers]);

  const handleRefresh = useCallback(async () => {
    await fetchLayers();
  }, [fetchLayers]);

  const handleRequestToggleAssetPlanShareSettings = (
    event: React.SyntheticEvent<HTMLElement>,
    assetLayerId: string,
    usePlanShareSettings: boolean
  ) => {
    event.stopPropagation();

    if (!assetLayerId) {
      return;
    }

    setSelectedAssetPlanShareLayerId(assetLayerId);
    setSelectedAssetUsePlanShareSettings(usePlanShareSettings);
    setOpenPlanShareConfirm(true);
  };

  const handleToggleAssetPlanShareSettings = async () => {
    if (!selectedAssetPlanShareLayerId) {
      return;
    }

    try {
      setLoading(true);
      const response = selectedAssetUsePlanShareSettings
        ? await planManagementService.disableAssetLayerUsePlanShareSettings(selectedAssetPlanShareLayerId)
        : await planManagementService.enableAssetLayerUsePlanShareSettings(selectedAssetPlanShareLayerId);

      if (response?.ok) {
        await fetchLayers();
        setOpenPlanShareConfirm(false);
        setSelectedAssetPlanShareLayerId(null);
      }
    }
    catch (err) {
      console.error('Error toggling asset plan share settings:', err);
      setError('Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  };

  const handleRequestTogglePublicLayerActive = (
    event: React.SyntheticEvent<HTMLElement>,
    publicLayerId: string,
    isActive: boolean
  ) => {
    event.stopPropagation();

    if (!publicLayerId) {
      return;
    }

    setSelectedPublicLayerId(publicLayerId);
    setSelectedPublicLayerActive(isActive);
    setOpenActiveConfirm(true);
  };

  const handleTogglePublicLayerActive = async () => {
    if (!selectedPublicLayerId) {
      return;
    }

    try {
      setLoading(true);
      const layer = await organisatieService.getPublicLayer(selectedPublicLayerId);

      if (!layer) {
        throw new Error('Layer niet gevonden');
      }

      const response = await organisatieService.updatePublicMap({
        ...layer,
        active: !layer.active,
      });

      if (response?.ok) {
        await fetchLayers();
        setOpenActiveConfirm(false);
        setSelectedPublicLayerId(null);
      }
    }
    catch (err) {
      console.error('Error toggling public layer active state:', err);
      setError('Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  };

  const columns = useMemo<GridColDef<LayerViewModel>[]>(() => {
    const baseColumns: GridColDef<LayerViewModel>[] = [];

    if (type === LayerType.ASSET) {
      baseColumns.push({
        field: 'usePlanShareSettings',
        headerName: 'Plan deelinstellingen',
        flex: 2,
        renderCell: (params: any) => {
          const isEnabled = Boolean(params.row.usePlanShareSettings);
          return (
            <Tooltip
              title={
                isEnabled
                  ? 'Klik om plan deelinstellingen uit te zetten'
                  : 'Klik om plan deelinstellingen aan te zetten'
              }
              placement="right"
              arrow
            >
              <span>
                <Switch
                  size="small"
                  checked={isEnabled}
                  onChange={(event) => handleRequestToggleAssetPlanShareSettings(event, params.row.id, isEnabled)}
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: (theme) => theme.palette.success.main,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: (theme) => theme.palette.success.main,
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: (theme) => theme.palette.action.disabled,
                      opacity: 1,
                    },
                  }}
                />
              </span>
            </Tooltip>
          );
        }
      });
    }

    if (type === LayerType.PUBLIC) {
      baseColumns.push({
          field: "icon",
          headerName: "Actief",
          flex: 1,
          renderCell: (params) => {
            return params.row.active ? (
              <Tooltip title="Klik om kaartlaag te deactiveren" placement="right" arrow>
                <span>
                  <Switch
                    size="small"
                    checked={Boolean(params.row.active)}
                    onChange={(event) => handleRequestTogglePublicLayerActive(event, params.row.id, Boolean(params.row.active))}
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: (theme) => theme.palette.success.main,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: (theme) => theme.palette.success.main,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: (theme) => theme.palette.action.disabled,
                        opacity: 1,
                      },
                    }}
                  />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Klik om kaartlaag te activeren" placement="right" arrow>
                <span>
                  <Switch
                    size="small"
                    checked={Boolean(params.row.active)}
                    onChange={(event) => handleRequestTogglePublicLayerActive(event, params.row.id, Boolean(params.row.active))}
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: (theme) => theme.palette.success.main,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: (theme) => theme.palette.success.main,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: (theme) => theme.palette.action.disabled,
                        opacity: 1,
                      },
                    }}
                  />
                </span>
              </Tooltip>
            );
          },
        }
      );
    }

    if (type === LayerType.PUBLIC) {
      baseColumns.push({ field: 'sourceName', headerName: 'Bron', flex: 2 });
    }

    if (type === LayerType.ASSET) {
      baseColumns.push({ field: 'sourceLayerName', headerName: 'Bronnaam', flex: 3 });
    }

    if (type === LayerType.PUBLIC) {
      baseColumns.push({ field: 'layerName', headerName: 'Naam', flex: 3 });
    }
    baseColumns.push({ field: 'displayName', headerName: 'Schermnaam', flex: 3 });
    baseColumns.push({
      field: "type",
      headerName: "Type",
      flex: 1,
      renderCell: (params: any) => {
        const value = (params.value || '').toLowerCase();
        const variantClass = `type-badge-${value}`;
        return (
          <span className={`type-badge ${variantClass}`}>
            {value}
          </span>
        );
      },
    });

    if (type === LayerType.ASSET) {
      baseColumns.push({
        field: 'lastModified',
        headerName: 'Laatste wijziging',
        flex: 2,
        renderCell: (params: any) => {
          const value = params.row.lastModified;
          if (!value) return '';
          const date = new Date(value);
          return isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
        }
      });
    }

    if (type === LayerType.PUBLIC) {
      baseColumns.push({ field: 'url', headerName: 'Url', flex: 5 });
      baseColumns.push({
        field: 'lastModified',
        headerName: 'Laatste wijziging',
        flex: 2,
        renderCell: (params: any) => {
          const value = params.row.lastModified;
          if (!value) return '';
          const date = new Date(value);
          return isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
        }
      });
    }

    if (authService.isKeyUser()) {
      baseColumns.push({
        field: 'actions',
        headerName: '',
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<LayerViewModel>) => (
          <LayersActionMenu params={params} layerType={type} onRefresh={handleRefresh} />
        ),
      });
    }
  
    return baseColumns;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, handleRefresh]);

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage ?? ''}
      />

      {type === LayerType.PUBLIC && (
        <Box sx={{ mb: 2 }}>
          <Tooltip title="Voeg een nieuwe openbare kaartlaag toe" placement="right" arrow>
            <Button variant="contained" color="secondary" onClick={() => setOpenAdd(true)}>
              <Icon sx={{ mr: 1 }}>add</Icon>
              Openbare kaartlaag
            </Button>
          </Tooltip>
        </Box>
      )}

      {type === LayerType.ASSET && (
        <Box sx={{ mb: 2 }}>
          <Tooltip title="Voeg een nieuwe organisatie kaartlaag toe" placement="right" arrow>
            <Button variant="contained" color="secondary" onClick={() => navigate('/mijn/kaartlagen/importeren')}>
              <Icon sx={{ mr: 1 }}>add</Icon>
              Organisatie kaartlaag
            </Button>
          </Tooltip>
        </Box>
      )}

      <Box className="grid-responsive">

        {type === LayerType.PUBLIC && (
          <div className="search"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "8px",
            }}>
            <TextField
              id="search"
              label="Zoeken in kaartlagen"
              variant="filled"
              placeholder="Zoeken op naam, bron of url"
              value={searchText ?? ""}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  // searchText triggers fetchPublicMaps via useEffect, dus we hoeven hier niets extra's te doen
                }
              }}
              InputProps={{
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        clearSearchText();
                      }}
                      sx={{
                        p: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon fontSize="small">clear</Icon>
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: { xs: '100%', md: '35%' }, 
                backgroundColor: "#fff", // witte achtergrond
                "& .MuiFilledInput-root": {
                  backgroundColor: "#fff",
                  borderRadius: "4px",
                  "&:hover": { backgroundColor: "#fff" },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                  },
                },
                "& .MuiFilledInput-input": {
                  paddingRight: "32px", // ruimte voor het clear-icoon
                },
              }}
            />
          </div>
        )}

        <DataGrid
          rows={layers}
          columns={columns}
          getRowId={(row) => row.id || ''}
          disableRowSelectionOnClick
          disableColumnMenu
          pageSizeOptions={[5, 10, 25, 50, 100]}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          rowCount={totalCount}
          onPaginationModelChange={(model) => setPaginationModel(model)}
          loading={loading}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          slotProps={{
            noRowsOverlay: { message: type === LayerType.PUBLIC ? 'Geen openbare kaartlagen gevonden' : 'Geen organisatie kaartlagen gevonden' },
          }}
          sx={{ border: 0 }}
        />
      </Box>

      <DialogPublicLayer
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSubmit={handleAdd}
      />

      <DialogConfirm
        open={openActiveConfirm}
        title={selectedPublicLayerActive ? 'Kaartlaag deactiveren' : 'Kaartlaag activeren'}
        contentText={selectedPublicLayerActive
          ? 'Weet je zeker dat je deze kaartlaag wilt deactiveren?'
          : 'Weet je zeker dat je deze kaartlaag wilt activeren?'}
        confirmButtonText={'Ja dat weet ik zeker'}
        onClose={() => {
          setOpenActiveConfirm(false);
          setSelectedPublicLayerId(null);
        }}
        onConfirm={handleTogglePublicLayerActive}
      />

      <DialogConfirm
        open={openPlanShareConfirm}
        title={selectedAssetUsePlanShareSettings ? 'Gebruik eigen deelinstellingen' : 'Gebruik plan deelinstellingen'}
        contentText={selectedAssetUsePlanShareSettings
          ? `Weet je zeker dat je de huidige plan deelinstellingen wilt uitschakelen voor deze kaartlaag? 
                Let op! Hierdoor wijzigen de deelinstellingen van deze kaartlaag niet meer automatisch mee maar kun je wel specifiek de deelinstellingen van deze kaartlaag aanpassen.`
          : `Weet je zeker dat je de deelinstellingen van plannen wilt gebruiken voor deze kaartlaag? 
                Let op! Hierdoor worden de deelinstellingen van deze kaartlaag gelijk aan die van het plan en worden specifieke deel instellingen overschreven.`}
        confirmButtonText={'Ja dat weet ik zeker'}
        onClose={() => {
          setOpenPlanShareConfirm(false);
          setSelectedAssetPlanShareLayerId(null);
        }}
        onConfirm={handleToggleAssetPlanShareSettings}
      />

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default Layers;
